import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, ExternalLink, MapPin, CheckCircle2, AlertTriangle,
  TrendingUp, TrendingDown, Sparkles, Plus, Download, Home, Layers,
  Building2, Calendar, Flame, Target, Wallet, ShieldCheck, ShieldAlert,
  ChevronRight, BarChart3, Scale, Droplet, Pin, LineChart, Cpu,
} from 'lucide-react';
import { api, AED_short } from '../lib/api';
import AgentAnalysisTab from './explainer/AgentAnalysisTab';

/**
 * Deal Explainer — TABBED design (v3, iteration 26).
 *   Landing = Summary. Then 8 dimension tabs + Evidence.
 *   Big fonts, bullets, no italic body, clickable comps per dimension.
 */
export default function ExplainerPage() {
  const { source, sourceId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [thinkingIdx, setThinkingIdx] = useState(0);

  const tab = params.get('tab') || 'summary';
  const setTab = (t) => { const p = new URLSearchParams(params); p.set('tab', t); setParams(p, { replace: true }); };

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setThinkingIdx(i => i + 1), 3500);
    return () => clearInterval(t);
  }, [loading]);

  useEffect(() => {
    let live = true;
    setLoading(true); setData(null);
    api.post('/trends/deal-radar/explain', { source, source_id: sourceId })
      .then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) toast.error(e.response?.data?.detail || 'Failed'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [source, sourceId]);

  const saveToAssets = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const it = data.item_snapshot || {};
      await api.post('/properties', {
        title: it.title || `${it.community || 'Dubai'} ${(it.bedrooms || 0)}BR`,
        community: it.community, property_type: it.property_type || 'apartment',
        bedrooms: it.bedrooms, size_sqft: it.size_sqft,
        asking_price_aed: it.price_aed, stage: 'sourcing',
        source_url: data.source_url, cover_photo_url: data.cover_photo_url,
        notes: `Iris · ${data.verdict || ''}`,
      });
      toast.success('Saved to Assets');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const runAnalyzer = () => {
    const it = data?.item_snapshot || {};
    const p = new URLSearchParams();
    if (it.community) p.set('community', it.community);
    if (it.bedrooms != null) p.set('bedrooms', String(it.bedrooms));
    if (it.size_sqft) p.set('size_sqft', String(it.size_sqft));
    if (it.price_aed) p.set('asking_price_aed', String(it.price_aed));
    if (it.property_type) p.set('property_type', it.property_type);
    if (data?.source_url) p.set('listing_url', data.source_url);
    nav(`/app/deal-analyzer?${p.toString()}`);
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const r = await api.post(`/trends/deal-radar/${source}/${sourceId}/pdf`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `iris-${sourceId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF failed'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" data-testid="explainer-page">
      <StickyHeader data={data} onBack={() => nav(-1)}/>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {loading ? <ThinkingState idx={thinkingIdx}/> : data ? (
          <>
            <Hero data={data}/>
            <ActionBar saving={saving} downloading={downloading}
              onSave={saveToAssets} onAnalyzer={runAnalyzer} onPdf={downloadPDF}/>
            <TabStrip tab={tab} setTab={setTab} data={data}/>
            <TabBody tab={tab} data={data} setTab={setTab}/>
          </>
        ) : (
          <div className="py-20 text-center text-slate-500">
            Could not load. <Link to="/app/trends" className="text-[#679286] underline">Back</Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HEADER + HERO
// ═══════════════════════════════════════════════════════════════

function StickyHeader({ data, onBack }) {
  return (
    <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200/70 px-4 py-3 flex items-center gap-3">
      <button onClick={onBack} data-testid="explainer-back"
        className="p-2 -ml-2 rounded-full hover:bg-slate-100 active:scale-95 transition">
        <ArrowLeft size={18}/>
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#93704A] font-semibold flex items-center gap-1">
          <Sparkles size={11}/> Iris AI Analysis
        </div>
        <div className="text-sm font-semibold truncate text-slate-900">
          {data?.item_snapshot?.title || data?.item_snapshot?.community || 'Analyzing…'}
        </div>
      </div>
      {data?.source_url && (
        <a href={data.source_url} target="_blank" rel="noreferrer"
          className="p-2 rounded-full hover:bg-slate-100 text-slate-600">
          <ExternalLink size={16}/>
        </a>
      )}
    </div>
  );
}

function Hero({ data }) {
  const it = data.item_snapshot || {};
  const score = data.hotness_score;
  const grad = score == null ? 'from-slate-400 to-slate-600'
             : score >= 80 ? 'from-emerald-500 to-teal-600'
             : score >= 65 ? 'from-amber-500 to-orange-500'
             : score >= 50 ? 'from-slate-400 to-slate-600'
             : 'from-red-400 to-red-600';
  return (
    <div className="mb-5 rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-sm">
      <div className="grid md:grid-cols-[300px_1fr]">
        {data.cover_photo_url ? (
          <div className="relative aspect-[4/3] md:aspect-auto bg-slate-100">
            <img src={data.cover_photo_url} alt="" className="w-full h-full object-cover"/>
          </div>
        ) : (
          <div className="aspect-[4/3] md:aspect-auto bg-gradient-to-br from-slate-100 to-slate-200 grid place-items-center text-slate-400 text-sm">No photo</div>
        )}
        <div className="p-5 md:p-6 flex flex-col justify-center">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2 flex-wrap">
            <MapPin size={12}/>
            <span className="font-medium text-slate-700">{it.community || '—'}</span>
            <span className="text-slate-300">·</span>
            <span>{it.property_type || '—'}</span>
            {it.bedrooms != null && <><span className="text-slate-300">·</span><span>{it.bedrooms} BR</span></>}
            {it.size_sqft && <><span className="text-slate-300">·</span><span>{Number(it.size_sqft).toLocaleString()} sqft</span></>}
          </div>
          {it.price_aed && <div className="font-serif text-4xl md:text-5xl text-slate-900 leading-none mb-1">{AED_short(it.price_aed)}</div>}
          {it.aed_per_sqft && <div className="text-sm text-slate-500 mb-4">AED {Math.round(it.aed_per_sqft).toLocaleString()}/sqft</div>}
          <div className="flex items-start gap-3 mt-2">
            <div className={`w-20 h-20 rounded-2xl grid place-items-center text-white shadow-md shrink-0 bg-gradient-to-br ${grad}`}>
              <div className="text-center leading-none">
                <div className="font-serif text-3xl">{score ?? '—'}</div>
                <div className="text-[9px] uppercase tracking-widest opacity-90 mt-1">score</div>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#679286] font-bold mb-1">Iris verdict</div>
              <div className="font-serif text-lg md:text-xl text-slate-900 leading-snug">{data.verdict}</div>
              {data.exit_strategy?.preferred && (
                <div className="mt-2 text-sm text-slate-600">
                  Preferred exit: <ExitBadge exit={data.exit_strategy.preferred}/>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExitBadge({ exit }) {
  const map = {
    flip:{l:'FLIP',c:'bg-orange-500'}, hold:{l:'HOLD',c:'bg-emerald-600'},
    either:{l:'FLIP/HOLD',c:'bg-sky-600'}, auction:{l:'AUCTION',c:'bg-purple-600'},
  };
  const cfg = map[exit] || {l:'—',c:'bg-slate-500'};
  return <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest text-white ${cfg.c}`}>{cfg.l}</span>;
}

function ActionBar({ saving, downloading, onSave, onAnalyzer, onPdf }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      <button onClick={onSave} disabled={saving} data-testid="explainer-save-asset"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#679286] text-white text-sm font-semibold hover:bg-[#557a70] disabled:opacity-60">
        {saving ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Save to Assets
      </button>
      <button onClick={onAnalyzer} data-testid="explainer-analyzer"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50">
        <Sparkles size={13}/> Open in Deal Analyzer
      </button>
      <button onClick={onPdf} disabled={downloading} data-testid="explainer-pdf"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
        {downloading ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>} PDF
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TABS
// ═══════════════════════════════════════════════════════════════

const TABS = [
  { key: 'summary',    label: 'Summary',      icon: Flame,        color: 'text-[#93704A]' },
  { key: 'agent',      label: 'Iris Agent',   icon: Cpu,          color: 'text-[#679286]' },
  { key: 'discount',   label: 'Discount',     icon: TrendingDown, color: 'text-orange-600' },
  { key: 'yield',      label: 'Yield',        icon: Wallet,       color: 'text-sky-600' },
  { key: 'momentum',   label: 'Momentum',     icon: LineChart,    color: 'text-emerald-600' },
  { key: 'supply',     label: 'Supply/Demand',icon: Scale,        color: 'text-purple-600' },
  { key: 'confidence', label: 'Confidence',   icon: ShieldCheck,  color: 'text-teal-600' },
  { key: 'liquidity',  label: 'Liquidity',    icon: Droplet,      color: 'text-blue-600' },
  { key: 'positioning',label: 'Positioning',  icon: Pin,          color: 'text-indigo-600' },
  { key: 'risks',      label: 'Risks & Next', icon: AlertTriangle,color: 'text-amber-600' },
  { key: 'evidence',   label: 'Evidence',     icon: Layers,       color: 'text-slate-700' },
];

function TabStrip({ tab, setTab, data }) {
  return (
    <div className="mb-4 sticky top-16 z-10 bg-white/95 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-sm p-1 flex flex-wrap gap-1 justify-center" data-testid="explainer-tabs">
      {TABS.map(t => {
        const on = tab === t.key;
        const Icon = t.icon;
        return (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            title={t.label}
            className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-[11px] md:text-xs font-semibold whitespace-nowrap transition ${
              on ? 'bg-[#1A2530] text-white shadow-md' : `${t.color} hover:bg-slate-100`
            }`}>
            <Icon size={12}/>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function TabBody({ tab, data, setTab }) {
  const { source, sourceId } = useParams();
  switch (tab) {
    case 'agent':       return <AgentAnalysisTab source={source} sourceId={sourceId}/>;
    case 'discount':    return <FactorTab data={data} factorKey="discount"    icon={TrendingDown} accent="orange"   title="Discount vs sold median" setTab={setTab}/>;
    case 'yield':       return <YieldTab data={data} setTab={setTab}/>;
    case 'momentum':    return <FactorTab data={data} factorKey="momentum"    icon={LineChart}    accent="emerald"  title="Community momentum" setTab={setTab}/>;
    case 'supply':      return <FactorTab data={data} factorKey="supply"      icon={Scale}        accent="purple"   title="Supply / demand pressure" setTab={setTab}/>;
    case 'confidence':  return <FactorTab data={data} factorKey="confidence"  icon={ShieldCheck}  accent="teal"     title="Data confidence" setTab={setTab}/>;
    case 'liquidity':   return <FactorTab data={data} factorKey="liquidity"   icon={Droplet}      accent="blue"     title="Verification & liquidity" setTab={setTab}/>;
    case 'positioning': return <FactorTab data={data} factorKey="positioning" icon={Pin}          accent="indigo"   title="Property positioning" setTab={setTab}/>;
    case 'risks':       return <RisksTab data={data}/>;
    case 'evidence':    return <EvidenceTab evidence={data.evidence}/>;
    default:            return <SummaryTab data={data} setTab={setTab}/>;
  }
}

// ═══════════════════════════════════════════════════════════════
// SUMMARY TAB
// ═══════════════════════════════════════════════════════════════

function SummaryTab({ data, setTab }) {
  const exit = data.exit_strategy || {};
  const ry = data.rental_yield || {};
  const positives = (data.positives || []).slice(0, 4);
  const risks = (data.risks || []).slice(0, 3);
  const snap = data.community_snapshot || {};
  const it = data.item_snapshot || {};
  const flipDisc = (snap.median_sold_psf && it.aed_per_sqft)
    ? ((it.aed_per_sqft - snap.median_sold_psf) / snap.median_sold_psf * 100).toFixed(1)
    : null;

  return (
    <div data-testid="tab-body-summary">
      {/* Flip vs Hold hero cards */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="p-5 rounded-3xl bg-gradient-to-br from-orange-50 to-amber-50/60 border-2 border-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-orange-500/15 grid place-items-center text-orange-600"><TrendingUp size={18}/></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-orange-700 font-bold">Flip case</div>
              <div className="font-serif text-3xl text-orange-900 leading-none mt-0.5">
                {exit.flip_projection_pct != null ? `${exit.flip_projection_pct > 0 ? '+' : ''}${exit.flip_projection_pct}%` : '—'}
              </div>
              <div className="text-xs text-orange-700 mt-1">projected ROI</div>
            </div>
          </div>
          <div className="text-sm text-slate-700">
            {flipDisc != null && <div>Discount vs sold median: <b className={flipDisc < 0 ? 'text-emerald-600' : 'text-red-600'}>{flipDisc}%</b></div>}
          </div>
        </div>
        <div className="p-5 rounded-3xl bg-gradient-to-br from-sky-50 to-blue-50/60 border-2 border-sky-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-sky-500/15 grid place-items-center text-sky-600"><Home size={18}/></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sky-700 font-bold">Hold case</div>
              <div className="font-serif text-3xl text-sky-900 leading-none mt-0.5">
                {ry.gross_pct != null ? `${ry.gross_pct}%` : '—'}
              </div>
              <div className="text-xs text-sky-700 mt-1">gross yield · {ry.band || '—'}</div>
            </div>
          </div>
          {ry.annual_rent_estimate_aed > 0 && (
            <div className="text-sm text-slate-700">Est. annual rent: <b>{AED_short(ry.annual_rent_estimate_aed)}</b></div>
          )}
          {exit.hold_years_to_breakeven && (
            <div className="text-sm text-slate-700">Breakeven: <b>{exit.hold_years_to_breakeven} years</b></div>
          )}
        </div>
      </div>

      {/* Top reasons — bulleted, big font */}
      {positives.length > 0 && (
        <div className="mb-4 p-5 rounded-3xl bg-emerald-50/60 border border-emerald-200">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-3 flex items-center gap-1">
            <CheckCircle2 size={12}/> Why this deal works
          </div>
          <ul className="space-y-2.5 text-base text-slate-800">
            {positives.map((p, i) => <li key={i} className="flex gap-2.5"><span className="text-emerald-600 font-bold shrink-0">✓</span><span>{p}</span></li>)}
          </ul>
        </div>
      )}

      {/* Top risks */}
      {risks.length > 0 && (
        <div className="mb-4 p-5 rounded-3xl bg-amber-50/60 border border-amber-200">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-3 flex items-center gap-1">
            <AlertTriangle size={12}/> Watch out for
          </div>
          <ul className="space-y-2.5 text-base text-slate-800">
            {risks.map((r, i) => <li key={i} className="flex gap-2.5"><span className="text-amber-600 font-bold shrink-0">!</span><span>{r}</span></li>)}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => setTab('evidence')} data-testid="summary-see-evidence"
          className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[#1A2530] text-white text-sm font-semibold hover:bg-[#2a3540]">
          See all evidence <ChevronRight size={14}/>
        </button>
        <button onClick={() => setTab('discount')}
          className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50">
          Drill into each factor <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// GENERIC FACTOR TAB (used for 6 of the 8 factors)
// ═══════════════════════════════════════════════════════════════

function findBreakdown(breakdown, keywords) {
  const kws = keywords.map(k => k.toLowerCase());
  return (breakdown || []).find(b =>
    kws.some(k => (b.factor || '').toLowerCase().includes(k))
  );
}

const FACTOR_MAP = {
  discount:    { keywords: ['discount', 'sold median', 'psf'],           evidence_tab: 'sold' },
  momentum:    { keywords: ['momentum', 'appreciat'],                    evidence_tab: null },
  supply:      { keywords: ['supply', 'demand', 'pressure'],             evidence_tab: 'live' },
  confidence:  { keywords: ['confidence', 'data', 'penalty', 'sample'],  evidence_tab: 'sold' },
  liquidity:   { keywords: ['liquid', 'verif', 'days'],                  evidence_tab: null },
  positioning: { keywords: ['position', 'attribute', 'price', 'size'],   evidence_tab: 'live' },
};

const ACCENTS = {
  orange:  { border: 'border-orange-200',  bg: 'bg-orange-50/60',  text: 'text-orange-700',  chip: 'bg-orange-500' },
  sky:     { border: 'border-sky-200',     bg: 'bg-sky-50/60',     text: 'text-sky-700',     chip: 'bg-sky-500' },
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50/60', text: 'text-emerald-700', chip: 'bg-emerald-500' },
  purple:  { border: 'border-purple-200',  bg: 'bg-purple-50/60',  text: 'text-purple-700',  chip: 'bg-purple-500' },
  teal:    { border: 'border-teal-200',    bg: 'bg-teal-50/60',    text: 'text-teal-700',    chip: 'bg-teal-500' },
  blue:    { border: 'border-blue-200',    bg: 'bg-blue-50/60',    text: 'text-blue-700',    chip: 'bg-blue-500' },
  indigo:  { border: 'border-indigo-200',  bg: 'bg-indigo-50/60',  text: 'text-indigo-700',  chip: 'bg-indigo-500' },
};

function FactorTab({ data, factorKey, icon: Icon, accent, title, setTab }) {
  const info = FACTOR_MAP[factorKey];
  const b = findBreakdown(data.score_breakdown, info.keywords);
  const a = ACCENTS[accent] || ACCENTS.sky;
  const points = b?.points;
  const evidence = data.evidence || {};
  const compCount = info.evidence_tab === 'sold' ? (evidence.sold_comps || []).length
                  : info.evidence_tab === 'live' ? (evidence.live_comps || []).length
                  : 0;
  const noteBullets = b?.note ? String(b.note).split(/(?<=[.!?])\s+/).filter(x => x.trim()) : [];

  return (
    <div data-testid={`tab-body-${factorKey}`}>
      <div className={`p-6 rounded-3xl border-2 ${a.border} ${a.bg} mb-4`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-2xl ${a.chip} text-white grid place-items-center shrink-0`}>
            <Icon size={24}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs uppercase tracking-widest ${a.text} font-bold`}>Factor</div>
            <div className="font-serif text-2xl text-slate-900 leading-tight">{title}</div>
          </div>
          {points != null && (
            <div className="text-right">
              <div className={`font-mono font-bold text-3xl ${points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {points > 0 ? '+' : ''}{points}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">points</div>
            </div>
          )}
        </div>
      </div>

      {noteBullets.length > 0 && (
        <div className="p-5 rounded-3xl bg-white border border-slate-200 mb-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">What Iris found</div>
          <ul className="space-y-2.5 text-base text-slate-800">
            {noteBullets.map((s, i) => (
              <li key={i} className="flex gap-2.5">
                <span className={`w-1.5 h-1.5 rounded-full ${a.chip} mt-2.5 shrink-0`}/>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!b && (
        <div className="p-5 rounded-3xl bg-white border border-slate-200 text-sm text-slate-500 mb-4">
          Iris didn&apos;t flag this factor for this deal — either the signal was neutral or data was insufficient.
        </div>
      )}

      {info.evidence_tab && compCount > 0 && (
        <button onClick={() => setTab('evidence')}
          data-testid={`factor-${factorKey}-see-evidence`}
          className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border-2 border-dashed border-slate-300 hover:border-[#679286] hover:bg-slate-50 transition group">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Layers size={16} className="text-slate-500 group-hover:text-[#679286]"/>
            <span className="font-semibold">Backed by {compCount} {info.evidence_tab === 'sold' ? 'sold comps' : 'live listings'}</span>
          </div>
          <ChevronRight size={16} className="text-slate-400 group-hover:text-[#679286]"/>
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// YIELD TAB (custom — richer than generic factor)
// ═══════════════════════════════════════════════════════════════

function YieldTab({ data, setTab }) {
  const ry = data.rental_yield || {};
  const mc = data.market_context?.rental_yield_analysis || {};
  const b = findBreakdown(data.score_breakdown, ['yield', 'rental hold', 'hold viability']);
  const noteBullets = b?.note ? String(b.note).split(/(?<=[.!?])\s+/).filter(x => x.trim()) : [];
  const bandColor = ry.band === 'strong' ? 'text-emerald-600' :
                   ry.band === 'healthy-high' || ry.band === 'healthy' ? 'text-sky-600' :
                   ry.band === 'weak' ? 'text-amber-600' : 'text-slate-500';
  return (
    <div data-testid="tab-body-yield">
      <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-blue-50/60 border-2 border-sky-200 mb-4">
        <div className="flex items-baseline gap-3 mb-1">
          <div className="font-serif text-6xl text-sky-900 leading-none">{ry.gross_pct ?? '—'}%</div>
          <div className="text-lg text-sky-700">gross yield</div>
        </div>
        <div className={`text-sm font-semibold uppercase tracking-wider mt-1 ${bandColor}`}>Band: {ry.band || 'unknown'}</div>
        {ry.cash_flow_verdict && <div className="text-base text-slate-700 mt-3">&ldquo;{ry.cash_flow_verdict}&rdquo;</div>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {ry.annual_rent_estimate_aed && <StatTile label="Est. annual rent" value={AED_short(ry.annual_rent_estimate_aed)}/>}
        {ry.sample_count != null && <StatTile label="DLD rent samples" value={ry.sample_count}/>}
        {mc.live_median_asking_rent_psf != null && <StatTile label="Live asking psf" value={`AED ${Math.round(mc.live_median_asking_rent_psf).toLocaleString()}`}/>}
        {mc.dld_median_rent_psf != null && <StatTile label="DLD median psf" value={`AED ${Math.round(mc.dld_median_rent_psf).toLocaleString()}`}/>}
      </div>

      {noteBullets.length > 0 && (
        <div className="p-5 rounded-3xl bg-white border border-slate-200 mb-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Iris analysis</div>
          <ul className="space-y-2.5 text-base text-slate-800">
            {noteBullets.map((s, i) => (
              <li key={i} className="flex gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-sky-500 mt-2.5 shrink-0"/><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}

      <button onClick={() => setTab('evidence')} data-testid="yield-see-rent-comps"
        className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border-2 border-dashed border-slate-300 hover:border-sky-500 transition group">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Wallet size={16}/> <span className="font-semibold">See rent contracts & listings backing this yield</span>
        </div>
        <ChevronRight size={16} className="text-slate-400 group-hover:text-sky-600"/>
      </button>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="p-3 rounded-2xl bg-white border border-slate-200">
      <div className="font-serif text-xl text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RISKS & NEXT STEPS TAB
// ═══════════════════════════════════════════════════════════════

function RisksTab({ data }) {
  const risks = data.risks || [];
  const steps = data.suggested_next_steps || [];
  return (
    <div data-testid="tab-body-risks">
      {risks.length > 0 ? (
        <div className="mb-4 p-5 rounded-3xl bg-amber-50/60 border border-amber-200">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-3 flex items-center gap-1">
            <AlertTriangle size={12}/> Risks to check
          </div>
          <ul className="space-y-3 text-base text-slate-800">
            {risks.map((r, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold grid place-items-center shrink-0">!</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="p-5 rounded-3xl bg-white border border-slate-200 text-sm text-slate-500 mb-4">No specific risks flagged.</div>
      )}
      {steps.length > 0 && (
        <div className="p-5 rounded-3xl bg-gradient-to-br from-[#93704A]/8 to-white border border-[#93704A]/20">
          <div className="text-xs uppercase tracking-widest text-[#93704A] font-bold mb-3 flex items-center gap-1">
            <Target size={12}/> Suggested next steps
          </div>
          <ol className="space-y-3 text-base text-slate-800">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#93704A] text-white text-sm font-bold grid place-items-center shrink-0">{i + 1}</span>
                <span className="pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EVIDENCE TAB
// ═══════════════════════════════════════════════════════════════

function EvidenceTab({ evidence }) {
  const [sub, setSub] = useState('sold');
  const e = evidence || {};
  const groups = {
    sold: { label: 'DLD sold', rows: e.sold_comps || [], priceLabel: 'Price' },
    live: { label: 'Live sale', rows: e.live_comps || [], priceLabel: 'Ask' },
    rentTx: { label: 'DLD rent', rows: e.rental_transactions || [], priceLabel: 'Annual' },
    rentLive: { label: 'Live rent', rows: e.rental_listings || [], priceLabel: 'Ask' },
  };
  const rows = groups[sub].rows;
  const priceLabel = groups[sub].priceLabel;
  return (
    <div data-testid="tab-body-evidence">
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {Object.entries(groups).map(([k, g]) => (
          <button key={k} onClick={() => setSub(k)}
            data-testid={`evidence-sub-${k}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition ${
              sub === k ? 'bg-[#1A2530] text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}>
            {g.label} <span className="text-[11px] opacity-80">{g.rows.length}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl">
            No {groups[sub].label.toLowerCase()} data available.
          </div>
        ) : rows.map((r, i) => <EvidenceRow key={i} row={r} priceLabel={priceLabel}/>)}
      </div>
    </div>
  );
}

function EvidenceRow({ row, priceLabel }) {
  const url = row.listing_url;
  const dateStr = row.transaction_date ? new Date(row.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : null;
  const inner = (
    <div className="grid grid-cols-[1fr_auto] gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#679286] hover:shadow-sm transition">
      <div className="min-w-0">
        <div className="text-base font-semibold text-slate-900 truncate flex items-center gap-1.5">
          {row.building || row.title || row.community || 'Untitled'}
          {row.verified && <CheckCircle2 size={13} className="text-emerald-600 shrink-0"/>}
        </div>
        <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
          {row.community && <span className="inline-flex items-center gap-0.5"><MapPin size={11}/>{row.community}</span>}
          {row.bedrooms != null && <span>{row.bedrooms} BR</span>}
          {row.size_sqft && <span>{Number(row.size_sqft).toLocaleString()} sqft</span>}
          {dateStr && <span className="inline-flex items-center gap-0.5"><Calendar size={11}/>{dateStr}</span>}
        </div>
      </div>
      <div className="text-right shrink-0 flex flex-col items-end justify-center">
        <div className="font-mono text-base font-bold text-slate-900">{AED_short(row.price_aed)}</div>
        {row.psf && <div className="text-xs text-slate-500">AED {Math.round(row.psf).toLocaleString()}/sqft</div>}
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{priceLabel}</div>
      </div>
    </div>
  );
  return url ? <a href={url} target="_blank" rel="noreferrer" className="block" title="Open source listing">{inner}</a> : inner;
}

// ═══════════════════════════════════════════════════════════════
// LOADING STATE
// ═══════════════════════════════════════════════════════════════

const THINKING = [
  { icon: Building2, text: 'Fetching listing details from source…' },
  { icon: Layers,    text: 'Pulling recent DLD sold transactions…' },
  { icon: Home,      text: 'Cross-referencing live listings…' },
  { icon: Wallet,    text: 'Computing rental yield from DLD rent contracts…' },
  { icon: TrendingUp,text: 'Analyzing 30-day community momentum…' },
  { icon: Target,    text: 'Building flip vs hold thesis…' },
  { icon: ShieldCheck,text: 'Validating data confidence…' },
  { icon: Sparkles,  text: 'Composing the final verdict…' },
];

function ThinkingState({ idx }) {
  const visibleCount = Math.min(THINKING.length, idx + 1);
  return (
    <div className="py-16 max-w-lg mx-auto">
      <div className="flex flex-col items-center mb-8">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#679286] to-[#4d7266] grid place-items-center mb-4 shadow-lg">
          <Sparkles size={26} className="text-white animate-pulse"/>
        </div>
        <div className="text-xl font-serif text-slate-800">Iris is analyzing this deal</div>
        <div className="text-sm text-slate-500 mt-1">~30-60s first run · instant next time</div>
      </div>
      <div className="space-y-2">
        {THINKING.slice(0, visibleCount).map((m, i) => {
          const Icon = m.icon;
          const active = i === visibleCount - 1;
          return (
            <div key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${
                active ? 'bg-white shadow-md border border-[#679286]/30'
                       : 'bg-white/60 border border-transparent opacity-70'
              }`}>
              <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                active ? 'bg-[#679286]/15 text-[#679286]' : 'bg-emerald-100 text-emerald-600'
              }`}>
                {active ? <Loader2 size={15} className="animate-spin"/> : <CheckCircle2 size={15}/>}
              </div>
              <div className="text-sm text-slate-700 flex-1">{m.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
